import ReactPaginate from "react-paginate";
import { TFormValues, TTableColumn } from "../../types/common";
import {
  CTable,
  CTableBody,
  CTableDataCell,
  CTableFoot,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import { TableFilter } from "./TableFilter";
import styled from "styled-components";
import { beautify, getStatusColor, numberFormat } from "../../utils/helpers";
import { Select, Tag } from "antd";
import { EmptyWrapper } from "./Elements";
import { useState } from "react";

type TTableProps = {
  loading: boolean;
  count: number;
  tableTitle?: string;
  tableFooter?: any;
  tableTotals?: Record<string, any>;
  tableColumns: TTableColumn[];
  tableData: any[];
  hideFilters?: boolean;
  filters?: any;
  onFilterChange?: any;
  updateFormValues?(obj: TFormValues): void;
  tableStyle?: any;
  status?: any;
  noEmptyText?: boolean;
  showPagination?: boolean;
  tableHeaderItem?: any;
  pageItemsSize?: number;
};

/* the placeholder rows keep the column geometry while the request is in
   flight, so the table does not collapse and jump back on arrival */
const SKELETON_ROWS = 6;
const SKELETON_WIDTHS = [72, 116, 88, 132, 96];

export const Table = (props: TTableProps) => {
  const {
    tableData,
    tableColumns,
    tableFooter,
    tableTotals,
    tableTitle,
    loading,
    count,
    filters,
    hideFilters,
    tableStyle,
    onFilterChange,
    status,
    noEmptyText = true,
    showPagination = true,
    tableHeaderItem,
    pageItemsSize = 10,
  } = props;

  const [pageSize, setPageSize] = useState(pageItemsSize);

  const onChange = (key: string, value: string | number) => {
    const updatedFilters = { ...filters };
    delete updatedFilters.page;
    if (value === "" || value === null || value === undefined) {
      delete updatedFilters[key];
      if (onFilterChange) onFilterChange(updatedFilters);
      return;
    } else {
      if (onFilterChange) onFilterChange({ ...updatedFilters, [key]: value });
    }
  };

  const startValue =
    ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) + 1;
  const endValue =
    ((filters?.page || 1) - 1) * (filters?.page_size || pageSize) +
    tableData?.length;

  /* a table that paginates client side asks for neither a page size nor a
     page list, so the whole foot stands down with the pagination */
  const hasRows = !loading && !!tableData?.length;
  const showFooter = showPagination && hasRows && !!count;

  const PaginationBlock = () => {
    return (
      <PaginationArea>
        <ReactPaginate
          breakLabel="..."
          previousLabel={
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          }
          onPageChange={({ selected }) => onChange("page", selected + 1)}
          pageRangeDisplayed={3}
          pageCount={Math.ceil(count / (filters?.page_size || pageSize))}
          marginPagesDisplayed={2}
          nextLabel={
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          }
          renderOnZeroPageCount={null}
          containerClassName="pagination"
          pageClassName="page-item"
          pageLinkClassName="page-link"
          previousClassName="page-item nav-arrow"
          previousLinkClassName="page-link"
          nextClassName="page-item nav-arrow"
          nextLinkClassName="page-link"
          activeClassName="active"
          disabledClassName="disabled"
          forcePage={(filters?.page || 1) - 1}
          breakClassName="page-item"
          breakLinkClassName="page-link"
        />
      </PaginationArea>
    );
  };

  return (
    <TableCard>
      {/* ── Card head: identity on the leading edge, actions on the trailing ── */}
      {(tableTitle || status || tableHeaderItem) && (
        <Toolbar>
          <div className="toolbar-head">
            {tableTitle && <span className="accent" />}
            {tableTitle && <span className="title">{tableTitle}</span>}
            {status && (
              <Tag className="capitalize" color={getStatusColor(status)}>
                {beautify(status)}
              </Tag>
            )}
          </div>

          {tableHeaderItem && (
            <div className="toolbar-actions">{tableHeaderItem}</div>
          )}
        </Toolbar>
      )}

      {/* ── Table ── */}
      <div className="table-scroll" style={{ ...tableStyle }}>
        <CTable style={{ ...tableStyle }}>
          <CTableHead>
            <CTableRow>
              <CTableHeaderCell scope="col" className="row-index-head">
                #
              </CTableHeaderCell>
              {tableColumns?.map((item) => (
                <CTableHeaderCell
                  key={item.id}
                  className={item?.noWrap ? "no-wrap" : ""}
                  scope="col"
                >
                  <span className="capitalize">{item.header}</span>
                </CTableHeaderCell>
              ))}
            </CTableRow>

            {!hideFilters && (
              <CTableRow className="filter-row">
                <CTableHeaderCell scope="col" className="row-index-head" />
                {tableColumns?.map((row, i) =>
                  row.filter ? (
                    <CTableHeaderCell key={i}>
                      <TableFilter
                        name={row.filter?.key ? row.filter?.key : row.id}
                        onChange={onChange}
                        updateFormValues={onFilterChange}
                        updateFilterValues={onFilterChange}
                        filters={filters}
                        selectOptions={row.filter?.options}
                        {...row.filter}
                      />
                    </CTableHeaderCell>
                  ) : (
                    <CTableHeaderCell key={i} />
                  ),
                )}
              </CTableRow>
            )}
          </CTableHead>

          <CTableBody>
            {loading ? (
              Array.from({ length: SKELETON_ROWS }).map((_, rowIndex) => (
                <CTableRow key={`skeleton-${rowIndex}`} className="data-row">
                  <CTableDataCell className="row-index">
                    <span className="skeleton" style={{ width: 14 }} />
                  </CTableDataCell>
                  {tableColumns?.map((column, columnIndex) => (
                    <CTableDataCell key={column.id}>
                      <span
                        className="skeleton"
                        style={{
                          width:
                            SKELETON_WIDTHS[
                              (columnIndex + rowIndex) % SKELETON_WIDTHS.length
                            ],
                        }}
                      />
                    </CTableDataCell>
                  ))}
                </CTableRow>
              ))
            ) : tableData?.length ? (
              tableData.map((item, idx) => (
                <CTableRow key={item.id || idx} className="data-row">
                  <CTableDataCell className="row-index">
                    {item?.index || startValue + idx}
                  </CTableDataCell>
                  {tableColumns.map((column) => (
                    <CTableDataCell
                      key={column.id}
                      className={column?.noWrap ? "no-wrap" : ""}
                    >
                      {column.render
                        ? column.render(item)
                        : item[column.id] ||
                          (noEmptyText && <EmptyWrapper>Empty</EmptyWrapper>)}
                    </CTableDataCell>
                  ))}
                </CTableRow>
              ))
            ) : (
              <CTableRow>
                <CTableDataCell
                  colSpan={tableColumns?.length + 1}
                  className="text-center state-cell"
                >
                  <div className="empty-state">
                    <span className="empty-badge">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect x="3" y="3" width="7" height="7" rx="1.5" />
                        <rect x="14" y="3" width="7" height="7" rx="1.5" />
                        <rect x="3" y="14" width="7" height="7" rx="1.5" />
                        <rect x="14" y="14" width="7" height="7" rx="1.5" />
                      </svg>
                    </span>
                    <p>No data available</p>
                    <span className="empty-hint">
                      There are no records to display right now.
                    </span>
                  </div>
                </CTableDataCell>
              </CTableRow>
            )}
          </CTableBody>

          {tableTotals && Object.keys(tableTotals).length > 0 && (
            <CTableFoot className="totals-foot">
              <CTableRow>
                <CTableHeaderCell scope="row" className="row-index-head">
                  Totals
                </CTableHeaderCell>
                {tableColumns?.map((column) => (
                  <CTableHeaderCell
                    key={column.id}
                    className={column?.noWrap ? "no-wrap" : ""}
                  >
                    <span className="capitalize bold">
                      {tableTotals[column.id]}
                    </span>
                  </CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableFoot>
          )}
          {tableFooter && (
            <CTableFoot>
              <CTableRow>
                <CTableHeaderCell scope="row" className="row-index-head">
                  #
                </CTableHeaderCell>
                {tableColumns?.map((column) => (
                  <CTableHeaderCell
                    key={column.id}
                    className={column?.noWrap ? "no-wrap" : ""}
                  >
                    <span className="capitalize">
                      {tableFooter?.[column.id]}
                    </span>
                  </CTableHeaderCell>
                ))}
              </CTableRow>
            </CTableFoot>
          )}
        </CTable>
      </div>

      {/* ── Card foot: the range reads on the left, the controls on the right ── */}
      {showFooter && (
        <CardFoot>
          <span className="range">
            Showing{" "}
            <strong>
              {numberFormat(startValue)}&ndash;{numberFormat(endValue)}
            </strong>{" "}
            of <strong>{numberFormat(count)}</strong>
          </span>

          <div className="foot-controls">
            <Select
              options={[
                { label: "10 / page", value: 10 },
                { label: "20 / page", value: 20 },
                { label: "50 / page", value: 50 },
                { label: "100 / page", value: 100 },
                { label: "200 / page", value: 200 },
              ]}
              onChange={(value) => {
                setPageSize(value);
                onFilterChange({ ...filters, page_size: value });
              }}
              value={filters?.page_size || pageSize}
              size="small"
              variant="borderless"
              className="page-size-select"
            />

            <PaginationBlock />
          </div>
        </CardFoot>
      )}
    </TableCard>
  );
};

/* ================================================================
   STYLED COMPONENTS
   The tokens come from the design system in landing.css (--lp-*), so the
   register tables read the same as the dashboard panels.
   ================================================================ */

const TableCard = styled.div`
  border-radius: var(--lp-r-md);
  overflow: hidden;
  background: var(--lp-raised);
  border: 1px solid var(--lp-border);
  box-shadow: var(--lp-shadow-sm);

  /* ── Scroll wrapper ── */
  .table-scroll {
    overflow-x: auto;

    &::-webkit-scrollbar {
      height: 8px;
    }
    &::-webkit-scrollbar-track {
      background: var(--lp-bg-alt);
    }
    &::-webkit-scrollbar-thumb {
      background: #c3d0e1;
      border-radius: var(--lp-r-pill);

      &:hover {
        background: #a8b9ce;
      }
    }
  }

  /* ── Reset CTable defaults ──
     an auto width lets every column take the space its own content needs and
     the table overflow horizontally; the min-width keeps it filling the card
     when the columns are narrow enough to fit. */
  table {
    width: auto;
    min-width: 100%;
    margin-bottom: 0;
    border-collapse: separate;
    border-spacing: 0;
    table-layout: auto;
    font-variant-numeric: tabular-nums;
    --cui-table-bg: transparent;
    --cui-table-striped-bg: transparent;
  }

  /* cells size to their content rather than wrapping into a narrower column */
  th,
  td {
    white-space: nowrap;
  }

  /* ────────────────────────────────────────
     THEAD
     ──────────────────────────────────────── */
  thead tr:first-child th {
    background: var(--lp-tile);
    color: var(--lp-muted);
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    padding: 11px 16px;
    border-bottom: 1px solid var(--lp-border);
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead .filter-row th {
    background: var(--lp-tile);
    padding: 0 8px 10px;
    border-bottom: 1px solid var(--lp-border);
    white-space: nowrap;
  }

  /* the filter controls sit inside a header band, so they run a size down on
     the shared 36px form control and carry the card radius */
  thead .filter-row {
    .form-control,
    .ant-picker,
    .ant-btn,
    .ant-select-single .ant-select-selector {
      height: 30px !important;
      min-height: 30px !important;
      font-size: 12px !important;
      border-radius: var(--lp-r-sm) !important;
      background: var(--lp-raised) !important;
      border-color: var(--lp-border) !important;
    }

    .ant-select-single .ant-select-selector .ant-select-selection-item,
    .ant-select-single .ant-select-selector .ant-select-selection-placeholder,
    .ant-picker input,
    .form-control::placeholder {
      line-height: 28px !important;
      font-size: 12px !important;
    }

    .ant-btn {
      padding: 0 10px !important;
    }
  }

  /* ────────────────────────────────────────
     TBODY
     ──────────────────────────────────────── */
  tbody {
    /* no column rules and no striping: the leading edge marks the row instead */
    tr.data-row {
      background: var(--lp-raised);
      transition: background-color 0.18s ease;

      &:hover {
        background: var(--lp-primary-soft);

        .row-index {
          color: var(--lp-primary);
          box-shadow:
            inset 3px 0 0 var(--lp-primary),
            inset -1px 0 0 var(--lp-border);
        }
      }

      td {
        border-bottom: 1px solid var(--lp-border);
      }

      &:last-child td {
        border-bottom: none;
      }
    }

    td {
      font-size: 13px;
      color: var(--lp-text);
      padding: 8px 16px;
      vertical-align: middle;
      line-height: 1.45;
    }

    /* the state rows span the table, so they keep the full width to centre in */
    .state-cell {
      white-space: normal;
    }

    /* the first column carries the label, so it reads a shade stronger */
    td:nth-child(2) {
      font-weight: 500;
    }

    .row-index {
      color: #9aa8bd;
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      font-size: 11px;
      width: 48px;
      text-align: center;
      transition:
        color 0.18s ease,
        box-shadow 0.18s ease;
    }
  }

  /* the row number stays in view while a wide register scrolls sideways;
     an inherited background keeps the cell on the row's own colour through
     the hover */
  .row-index,
  .row-index-head {
    position: sticky;
    left: 0;
    z-index: 1;
    background: inherit;
    box-shadow: inset -1px 0 0 var(--lp-border);
  }

  thead .row-index-head {
    z-index: 3;
  }

  thead tr:first-child th.row-index-head {
    z-index: 4;
  }

  /* ── States ── */
  .state-cell {
    padding: 56px 16px;
    border-bottom: none !important;
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    .empty-badge {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      margin-bottom: 8px;
      border-radius: var(--lp-r-pill);
      background: var(--lp-tile);
      color: var(--lp-primary);
    }

    p {
      margin: 0;
      font-size: 13px;
      font-weight: 700;
      color: var(--lp-text);
    }

    .empty-hint {
      font-size: 11px;
      color: var(--lp-muted);
    }
  }

  /* the loading placeholder bar */
  .skeleton {
    display: block;
    height: 10px;
    max-width: 100%;
    border-radius: var(--lp-r-pill);
    background: linear-gradient(
      90deg,
      var(--lp-tile) 25%,
      #e2eaf4 37%,
      var(--lp-tile) 63%
    );
    background-size: 400% 100%;
    animation: tableSkeleton 1.4s ease infinite;
  }

  @keyframes tableSkeleton {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0 50%;
    }
  }

  .no-wrap {
    white-space: nowrap;
  }

  /* ────────────────────────────────────────
     TFOOT
     ──────────────────────────────────────── */
  tfoot {
    tr {
      background: var(--lp-tile);
    }

    tr th {
      background: var(--lp-tile);
      border-top: 1px solid var(--lp-border);
      font-size: 13px;
      font-weight: 600;
      color: var(--lp-text);
      padding: 12px 16px;
      white-space: nowrap;
    }

    /* the totals row is the summary line, so it reads at full weight */
    &.totals-foot tr th {
      font-weight: 700;
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 13px 16px;
  border-bottom: 1px solid var(--lp-border);

  .toolbar-head {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }

  /* the accent bar matches the section heads on the forms and the dashboard */
  .accent {
    width: 3px;
    height: 18px;
    flex-shrink: 0;
    border-radius: var(--lp-r-pill);
    background: var(--lp-primary);
  }

  .title {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: var(--lp-text);
    text-transform: capitalize;
  }

  .toolbar-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
`;

const CardFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px 16px;
  border-top: 1px solid var(--lp-border);
  background: var(--lp-raised);

  .range {
    font-size: 11px;
    color: var(--lp-muted);
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    strong {
      color: var(--lp-text);
      font-weight: 600;
    }
  }

  .foot-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }

  /* the page size reads as a quiet chip rather than a second input */
  .page-size-select {
    .ant-select-selector {
      height: 28px !important;
      padding: 0 10px !important;
      border-radius: var(--lp-r-pill) !important;
      background: var(--lp-tile) !important;
    }

    .ant-select-selection-item {
      font-size: 11px !important;
      line-height: 28px !important;
      color: var(--lp-muted) !important;
    }

    .ant-select-arrow {
      color: var(--lp-muted) !important;
    }
  }
`;

const PaginationArea = styled.div`
  display: flex;
  align-items: center;
  padding-left: 8px;
  border-left: 1px solid var(--lp-border);

  .pagination {
    display: flex;
    align-items: center;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 2px;
  }

  .page-item {
    .page-link {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 28px;
      height: 28px;
      padding: 0 4px;
      border-radius: var(--lp-r-sm);
      font-size: 11px;
      font-weight: 600;
      color: var(--lp-muted);
      background: transparent;
      border: none;
      text-decoration: none;
      transition: all 0.15s ease;
      cursor: pointer;
      font-variant-numeric: tabular-nums;

      &:hover {
        background: var(--lp-tile);
        color: var(--lp-text);
      }
    }

    &.active .page-link {
      background: var(--lp-primary) !important;
      color: #fff !important;
      box-shadow: 0 2px 6px rgba(33, 90, 169, 0.28);
    }

    &.nav-arrow .page-link {
      color: var(--lp-muted);
      padding: 0 2px;

      &:hover {
        color: var(--lp-text);
        background: var(--lp-tile);
      }
    }

    &.disabled .page-link {
      opacity: 0.35;
      pointer-events: none;
    }
  }
`;
